import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Loader2, Save } from "lucide-react";

/**
 * Founder/admin edit modal for resources + new-grad resources.
 *
 * Props:
 *   resource     — full resource doc (must include resource_id + title)
 *   resourceType — "resource" | "newgrad-resource"
 *   open         — controlled boolean
 *   onClose      — close handler
 *   onSaved      — callback(updatedResource) after a successful save
 *
 * For "resource": edits title / description / url / image_url / tags.
 * For "newgrad-resource": edits title / content / url / image_url / tags / category.
 * Tags are entered as comma-separated values and split on save.
 */
const AdminEditResourceModal = ({ resource, resourceType, open, onClose, onSaved }) => {
  const isNewGrad = resourceType === "newgrad-resource";
  const bodyField = isNewGrad ? "content" : "description";

  const [form, setForm] = useState({
    title: "",
    [bodyField]: "",
    url: "",
    image_url: "",
    tags: "",
    category: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!resource) return;
    setForm({
      title: resource.title || "",
      [bodyField]: resource[bodyField] || "",
      url: resource.url || "",
      image_url: resource.image_url || "",
      tags: Array.isArray(resource.tags) ? resource.tags.join(", ") : (resource.tags || ""),
      category: resource.category || "",
    });
  }, [resource, bodyField]);

  if (!resource) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const segment = isNewGrad ? "newgrad-resources" : "resources";
      const body = {
        title: form.title.trim(),
        [bodyField]: (form[bodyField] || "").trim(),
        url: form.url.trim() || null,
        image_url: form.image_url.trim() || null,
        tags: form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      };
      if (isNewGrad && form.category) body.category = form.category.trim();
      const res = await axios.patch(`${API}/admin/moderation/${segment}/${resource.resource_id}`, body);
      toast.success("Saved");
      onSaved && onSaved(res.data);
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-edit-resource-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>
            Edit {isNewGrad ? "new-grad resource" : "resource"}
          </DialogTitle>
          <DialogDescription>
            Changes save immediately and update what members see in real time. Logged in the admin audit log.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label htmlFor="aer-title">Title</Label>
            <Input
              id="aer-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1.5 bg-white border-[#E5DFEC]"
              data-testid="aer-title-input"
            />
          </div>
          <div>
            <Label htmlFor="aer-body">{isNewGrad ? "Content" : "Description"}</Label>
            <Textarea
              id="aer-body"
              value={form[bodyField]}
              onChange={(e) => setForm({ ...form, [bodyField]: e.target.value })}
              className="mt-1.5 min-h-[140px] bg-white border-[#E5DFEC]"
              data-testid="aer-body-input"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="aer-url">Link URL</Label>
              <Input
                id="aer-url"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://..."
                className="mt-1.5 bg-white border-[#E5DFEC]"
                data-testid="aer-url-input"
              />
            </div>
            <div>
              <Label htmlFor="aer-image">Image URL</Label>
              <Input
                id="aer-image"
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="https://..."
                className="mt-1.5 bg-white border-[#E5DFEC]"
                data-testid="aer-image-input"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="aer-tags">Tags <span className="text-xs text-[#94A3B8]">(comma-separated)</span></Label>
            <Input
              id="aer-tags"
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="Regulatory, Alberta"
              className="mt-1.5 bg-white border-[#E5DFEC]"
              data-testid="aer-tags-input"
            />
          </div>
          {isNewGrad && (
            <div>
              <Label htmlFor="aer-category">Category</Label>
              <Input
                id="aer-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="onboarding, salary, interview..."
                className="mt-1.5 bg-white border-[#E5DFEC]"
                data-testid="aer-category-input"
              />
            </div>
          )}
          {form.image_url ? (
            <div className="border border-[#E5DFEC] rounded-xl overflow-hidden">
              <img src={form.image_url} alt="" className="w-full max-h-48 object-cover" />
            </div>
          ) : null}
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="rounded-full"
              data-testid="aer-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !form.title.trim()}
              className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full"
              data-testid="aer-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <><Save className="w-4 h-4 mr-1.5" />Save changes</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminEditResourceModal;
